import { useEffect, useState } from 'react';
import type { ViewMode } from '../types';

interface Props {
  viewMode: ViewMode;
}

interface Stat { label: string; value: number; }
interface Activity { event: string; email?: string; timestamp: string; }

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function DashboardModule(_props: Props) {
  const [stats, setStats] = useState<Stat[] | null>(null);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [apiOnline, setApiOnline] = useState<boolean | null>(null);

  useEffect(() => {
    fetch('/api/dashboard/summary', { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('summary failed');
        return res.json();
      })
      .then(data => {
        setStats(data.stats || []);
        setActivity(data.recent_activity || []);
        setApiOnline(true);
      })
      .catch(() => {
        setStats([]);
        setApiOnline(false);
      });
  }, []);

  return (
    <div className="dashboard-grid" data-testid="dashboard-live">
      {/* Live Stats */}
      <div className="dashboard-section dashboard-stats">
        <h3 className="dashboard-section-title">Overview (Live)</h3>
        <div className="stats-grid" data-testid="dashboard-stats-grid">
          {stats === null && <div className="dashboard-empty-state">Loading metrics…</div>}
          {stats !== null && stats.length === 0 && (
            <div className="dashboard-empty-state" data-testid="dashboard-stats-empty">No metrics available yet.</div>
          )}
          {stats !== null && stats.map((stat, i) => (
            <div key={i} className="stat-card" data-testid={`stat-card-${i}`}>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-value">{stat.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* System Status (real) */}
      <div className="dashboard-section">
        <h3 className="dashboard-section-title">System Status</h3>
        <div className="systems-grid">
          <div className="system-card" data-testid="system-status-api">
            <div className="system-status-dot" data-status={apiOnline === false ? 'offline' : apiOnline ? 'online' : 'warning'}></div>
            <div className="system-info">
              <div className="system-name">Command Center API</div>
              <div className="system-uptime">{apiOnline === null ? 'Checking…' : apiOnline ? 'Online' : 'Unreachable'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity (real audit log) */}
      <div className="dashboard-section dashboard-activity">
        <h3 className="dashboard-section-title">Recent Activity</h3>
        <div className="activity-list" data-testid="dashboard-activity-list">
          {activity.length === 0 && (
            <div className="dashboard-empty-state" data-testid="dashboard-activity-empty">No recent activity yet.</div>
          )}
          {activity.map((a, i) => (
            <div key={i} className="activity-item">
              <span className="activity-time">{formatTime(a.timestamp)}</span>
              <span className="activity-action">{a.event.replace(/_/g, ' ')}</span>
              <span className="activity-user">{a.email || ''}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
